import React, { Fragment, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { CurrentCoordinate } from 'react-stockcharts/lib/coordinates';
import { BollingerSeries, LineSeries } from 'react-stockcharts/lib/series';
import { hexToRGBA } from 'react-stockcharts/lib/utils';

interface IProps {
	type: string;
	index: number;
	yAccessor: any;
	color: string;
}

const styles = `
	@keyframes drawLineRightToLeft {
		0% {
			stroke-dasharray: 10000;
			stroke-dashoffset: 10000;
			opacity: 0;
			clip-path: inset(0 0 0 100%);
		}
		100% {
			stroke-dasharray: 10000;
			stroke-dashoffset: 0;
			opacity: 1;
			clip-path: inset(0 0 0 0);
		}
	}
	@keyframes fillFromXAxis {
		0% {
			opacity: 0;
			transform: scaleY(0);
			transform-origin: top;
		}
		100% {
			opacity: 1;
			transform: scaleY(1);
			transform-origin: top;
		}
	}
	.line-animation-right {
		animation: drawLineRightToLeft 2s ease-out forwards;
		stroke-linecap: round;
		stroke-linejoin: round;
		transform-origin: right;
		transform-box: fill-box;
		will-change: clip-path;
	}
	.line-hidden {
		opacity: 0;
		clip-path: inset(0 0 0 100%);
		visibility: hidden;
	}
	.area-animation {
		animation: fillFromXAxis 2s ease-out forwards;
		transform-box: fill-box;
		transform-origin: top;
	}
	.area-hidden {
		opacity: 0;
		visibility: hidden;
	}
`;

const RenderIndicatorsOnchart: React.FC<IProps> = (props) => {
	const { index, type, yAccessor, color } = props;
	const opacity = (100 - (index + 1) * 10) / 100;
	const indicatorColor = hexToRGBA(color, opacity);
	const [shouldAnimate, setShouldAnimate] = useState(false);
	const [isReady, setIsReady] = useState(false);
	const location = useLocation();
	useEffect(() => {
		// Add styles to document
		const styleSheet = document.createElement('style');
		styleSheet.innerText = styles;
		document.head.appendChild(styleSheet);

		// Wait for next frame to ensure styles are applied
		requestAnimationFrame(() => {
			setIsReady(true);
			// Start animation immediately after component is ready
			setShouldAnimate(true);
		});

		return () => {
			document.head.removeChild(styleSheet);
		};
	}, []);

	if (!isReady) {
		return null;
	}

	const renderLineSeries = (key: string) => (
		<Fragment key={key}>
			<LineSeries
				yAccessor={yAccessor}
				stroke={indicatorColor}
				className={shouldAnimate ? !location.pathname.includes('excel/export') && 'line-animation-right' : 'line-hidden'}
				strokeWidth={2}
			/>
			<CurrentCoordinate
				yAccessor={yAccessor}
				fill={indicatorColor}
				style={{
					transition: shouldAnimate ? 'opacity 800ms ease-out' : 'none',
					opacity: shouldAnimate ? 1 : 0
				}}
			/>
		</Fragment>
	);

	switch (type) {
		case 'ema':
			return renderLineSeries(`ema${index}`);
		case 'sma':
			return renderLineSeries(`sma${index}`);
		case 'tma':
			return renderLineSeries(`tma${index}`);
		case 'bb':
			return (
				<BollingerSeries
					yAccessor={yAccessor}
					stroke={{
						top: indicatorColor,
						middle: indicatorColor,
						bottom: indicatorColor
					}}
					fill={indicatorColor}
					key={`bb${index}`}
					className={shouldAnimate ? !location.pathname.includes('excel/export') && 'area-animation' : 'area-hidden'}
				/>
			);
		case 'wma':
			return renderLineSeries(`wma${index}`);

		default:
			return <></>;
	}
};

export default RenderIndicatorsOnchart;
